/** 2D affine matrix [a, b, c, d, e, f]: x' = a·x + c·y + e, y' = b·x + d·y + f (same as DOMMatrix / SVG). */
export type Matrix = readonly [number, number, number, number, number, number];

export const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

export function multiply(m: Matrix, n: Matrix): Matrix {
  // m ∘ n: apply n first, then m.
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export const translate = (x: number, y: number): Matrix => [1, 0, 0, 1, x, y];
export const scale = (sx: number, sy = sx): Matrix => [sx, 0, 0, sy, 0, 0];
export function rotateDeg(deg: number): Matrix {
  const r = (deg * Math.PI) / 180;
  // Snap right angles to exact values so geometry stays on the grid.
  const c = Math.abs(deg % 90) === 0 ? Math.round(Math.cos(r)) : Math.cos(r);
  const s = Math.abs(deg % 90) === 0 ? Math.round(Math.sin(r)) : Math.sin(r);
  return [c, s, -s, c, 0, 0];
}

export function apply(m: Matrix, x: number, y: number): { x: number; y: number } {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] };
}

/** Composes matrices left to right in reading order: compose(A, B, C) applies C, then B, then A. */
export function compose(...ms: Matrix[]): Matrix {
  return ms.reduce((acc, m) => multiply(acc, m), IDENTITY);
}
